import React, { useState , useEffect} from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import PrimaryButton from '../button/PrimaryButton';
import {
  COUNTRY_CODES,
  buildFullPhoneNumber,
  getInitialDialCode,
  getPhonePlaceholder,
  isValidLocalNumber,
} from '../../data/countryCodes';

interface VoucherFormCardProps {
  voucher: any;
  handleSubmit: (name: string, number: string) => Promise<void>;
  onCheckStatus?: () => void;
  checkStatusLoading?: boolean;
}

function VoucherFormCard({ voucher, handleSubmit, onCheckStatus, checkStatusLoading }: VoucherFormCardProps): JSX.Element {
  const [fullName, setFullName] = useState("");
  const [number, setNumber] = useState("");
  const countryCodeConfig = voucher?.country_code || { mode: 'fixed' as const, fixedDialCode: '+91' };
  const isDynamicCountryCode = countryCodeConfig.mode === 'dynamic';
  const countryMode = countryCodeConfig.mode;
  // The LoB's own starting dial code: its fixed one (+91 for India) or its
  // configured default (+60 for Malaysia) - never a hardcoded +91.
  const initialDialCode = getInitialDialCode(countryCodeConfig);
  const [countryCode, setCountryCode] = useState(initialDialCode);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [checked, setChecked] = useState(false);

  const isCbdc = voucher?.brand === 'ICICI_CBDC';
  const isMyp = voucher?.brand === 'MYP';

  // Pin the dial code to the LoB's own: a fixed LoB has no selector to change
  // it, and a dynamic LoB starts on its default. Also covers `voucher` (and its
  // country_code) arriving after the initial state above was set.
  useEffect(() => {
    setCountryCode(initialDialCode);
  }, [initialDialCode]);

  // Validation function
  const validateForm = (): boolean => {
    let formErrors: {[key: string]: string} = {};
    let isValid = true;

    if (!fullName.trim()) {
      formErrors.fullName = "Full name cannot be empty.";
      isValid = false;
    } else {
      if (fullName.length > 50) {
        formErrors.fullName = "Full name cannot be longer than 50 characters";
        isValid = false;
      }
    }

    if (!isValidLocalNumber(countryCode, number.trim(), countryMode)) {
      formErrors.number = "Enter a valid mobile number.";
      isValid = false;
    }

    if (!checked) {
      formErrors.checked = "You must accept the terms and conditions to proceed."
      isValid = false
    }

    setErrors(formErrors);
    return isValid;
  };

  // Handle form submission
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // The request payload keeps its original single-field shape - the
        // dial code is concatenated onto the number here, not sent
        // separately.
        await handleSubmit(fullName, buildFullPhoneNumber(countryCode, number.trim(), countryMode));
      } catch (error) {
        console.error("Error during form submission:", error);
      }
    }
  };

  const card_body = () => {
    if (voucher.pending) {
      return (
        <div className="py-6 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-5">
            <Clock className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {isCbdc ? 'Your Digital Rupee is being issued' : 'Your voucher is being issued'}
          </h2>
          <p className="text-sm text-gray-600 max-w-sm">
            We're still processing this request with the bank. This can take a little longer
            than usual - we'll notify you as soon as it's ready to use.
          </p>
          {onCheckStatus && (
            <button
              type="button"
              onClick={onCheckStatus}
              disabled={checkStatusLoading}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#1b312e] bg-white border-2 border-[#53978e]/40 rounded-lg hover:border-[#53978e] hover:bg-[#53978e]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${checkStatusLoading ? 'animate-spin' : ''}`} />
              {checkStatusLoading ? 'Checking...' : 'Check Status'}
            </button>
          )}
        </div>
      );
    }

    if (voucher.activated) {
      return (
        <div className="pt-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Balance History</h2>
          {voucher.transactions.map((item: any, index: number) => {
            const date = new Date(item.created_at);
            return (
              <div key={index} className="mb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-gray-900">{item.tenant_name}</div>
                    <div className="text-sm text-gray-600">{item.tenant_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{item.amount}</div>
                    <div className="text-sm text-gray-600">
                      {date.toLocaleString("default", { month: "short" })}{" "}
                      {date.getDate()}, {date.getFullYear()}{" "}
                      {date.toTimeString().slice(0, 5)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    } else {
      return (
        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="font-medium text-[#1b312e] text-lg  whitespace-nowrap">
              Your Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={fullName}
              onChange={(e) => {
                let value = e.target.value ? e.target.value.replace(/[^A-Za-z0-9-& ]/g, "") : "";
                setErrors((prevState) => ({
                  ...prevState,
                  fullName: ""
                }));
                setFullName(value);
              }}
              className={`mt-1 w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#53978e]/70 focus:border-transparent transition-all duration-200 border-gray-200 ${
                errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-green-800/60'
              }`}
              placeholder="Enter your full name"
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                {errors.fullName}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="number" className="font-medium text-[#1b312e] text-lg  whitespace-nowrap">
              Mobile Number
            </label>
            <div className="flex w-full">
              {isDynamicCountryCode ? (
                <select
                  id="countryCode"
                  name="countryCode"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  aria-label="Country code"
                  className="mt-1 items-center px-2 sm:px-3 py-3 sm:py-4 border-2 border-r-0 border-gray-200 bg-gray-50 text-gray-500 text-xs sm:text-sm rounded-l-lg flex-shrink-0 focus:outline-none"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={`${c.iso2}-${c.dialCode}`} value={c.dialCode}>
                      {c.dialCode} {c.iso2}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="inline-flex mt-1 items-center px-3 sm:px-4 py-3 sm:py-4 border-2 border-r-0 border-gray-200 bg-gray-50 text-gray-500 text-xs sm:text-sm rounded-l-lg flex-shrink-0">
                  {initialDialCode}
                </span>
              )}
              <input
                type="text"
                id="number"
                name="number"
                value={number}
                onChange={(e) => {
                  let value = e.target.value.toUpperCase().replace(/[^0-9]/g, "");
                  setErrors((prevState) => ({
                    ...prevState,
                    number: ""
                  }));
                  setNumber(value);
                }}
                className={`flex-1 min-w-0 mt-1 px-3 sm:px-4 py-3 sm:py-4 border-2 border-l-0 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-green-800/60 focus:border-transparent transition-all duration-200 text-sm sm:text-base ${
                  errors.number ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-green-800/60'
                }`}
                placeholder={getPhonePlaceholder(countryCode, countryMode)}
              />
            </div>
            {errors.number && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                {errors.number}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="checked"
                name="checked"
                checked={checked}
                onChange={(e) => {
                  setChecked(e.target.checked);
                  if (e.target.checked) {
                    setErrors((prevState) => ({
                      ...prevState,
                      checked: ""
                    }));
                  }
                }}
                className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="checked" className="text-sm text-gray-700">
                <span dangerouslySetInnerHTML={{
                  __html: "I have read the <strong>Terms & Conditions</strong> and agree to redeem this voucher to issue a <strong>Voucher</strong> against the entered mobile number"
                }} />
              </label>
            </div>
            {errors.checked && (
              <p className="text-red-500 text-sm mt-1">{errors.checked}</p>
            )}
          </div>

          <div className="pt-4">
            <PrimaryButton type="submit" className="w-full py-4 text-lg font-semibold">
              Get Voucher
            </PrimaryButton>
          </div>

          {/* Important Notice */}
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-100">
            <div className="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="#EAB308"
                className="bi bi-exclamation-triangle flex-shrink-0 mt-0.5 mr-3"
                viewBox="0 0 16 16"
              >
                <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.15.15 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.2.2 0 0 1-.054.06.1.1 0 0 1-.066.017H1.146a.1.1 0 0 1-.066-.017.2.2 0 0 1-.054-.06.18.18 0 0 1 .002-.183L7.884 2.073a.15.15 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767z" />
                <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
              </svg>
              <div>
                <p className="text-sm text-yellow-800 font-medium">Important Notice</p>
                <p className="text-xs text-yellow-600 mt-1">
                  {isCbdc ? (
                    <>
                      Your Digital Rupee will be issued to this mobile number.
                      To use it, install the "Digital Rupee by ICICI Bank" app
                      and complete Aadhaar-linked SIM and No-KYC verification.
                    </>
                  ) : isMyp ? (
                    <>
                      Your Voucher will be issued to this mobile number and
                      will reflect in the MYP wallet linked to it. Please make
                      sure the number is correct.
                    </>
                  ) : (
                    <>
                      You must have a mobile number linked to Google Pay to
                      receive an activated Voucher. The Voucher will
                      reflect in the Google Pay app linked to this mobile number.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </form>
      );
    }
  };


  return (
    <div className="bg-white md:h-full  rounded-2xl shadow-2xl  md:p-8 p-6 w-full max-w-lg border border-gray-100 flex flex-col">
      {card_body()}
    </div>
  );
}

export default VoucherFormCard; 